import {
  $,
  sync$,
  component$,
  useSignal,
  useTask$,
  type PropFunction,
} from "@builder.io/qwik";
import { Input } from "./input";
import { FormField } from "./form-field";

type OtpInputProps = {
  error?: string;
  label?: string;
  length?: number;
  value: string[];
  description?: string;
  onComplete$?: PropFunction<(value: string) => void>;
  onChange$?: PropFunction<(value: string[]) => void>;
};

const isDigit = (value: string) => /^\d$/.test(value);

export const OtpInput = component$<OtpInputProps>(
  ({
    value,
    error,
    label,
    length = 6,
    description,
    onChange$,
    onComplete$,
  }) => {
    const code = useSignal<string[]>(Array(length).fill(""));
    const focusIndex = useSignal<number>(0);
    const inputRefs = useSignal<HTMLInputElement[]>([]);

    // Initialize refs array
    useTask$(({ cleanup }) => {
      inputRefs.value = Array(length).fill(null);
      cleanup(() => {
        inputRefs.value = [];
      });
    });

    // Sync with external value
    useTask$(({ track }) => {
      track(() => value);
      if (value.length === length) {
        code.value = [...value];
      }
    });

    const updateCode = $((newCode: string[]) => {
      code.value = [...newCode];
      onChange$?.(newCode);

      if (newCode.every(isDigit)) {
        onComplete$?.(newCode.join(""));
      }
    });

    const focusInput = $((index: number) => {
      if (index >= 0 && index < length && inputRefs.value[index]) {
        inputRefs.value[index].focus();
        focusIndex.value = index;
      }
    });

    const handleInput = $((event: Event, index: number) => {
      const target = event.target as HTMLInputElement;
      const digit = target.value;

      if (isDigit(digit)) {
        const newCode = [...code.value];
        newCode[index] = digit;
        updateCode(newCode);

        if (index < length - 1) {
          focusInput(index + 1);
        }
      } else if (digit === "") {
        const newCode = [...code.value];
        newCode[index] = "";
        updateCode(newCode);
      }
    });

    const handleKeyDown = $((event: KeyboardEvent, index: number) => {
      switch (event.key) {
        case "Backspace":
          if (!code.value[index]) {
            event.preventDefault();
            if (index > 0) {
              const newCode = [...code.value];
              newCode[index - 1] = "";
              updateCode(newCode);
              focusInput(index - 1);
            }
          }
          break;
        case "ArrowLeft":
          event.preventDefault();
          focusInput(index - 1);
          break;
        case "ArrowRight":
          event.preventDefault();
          focusInput(index + 1);
          break;
      }
    });

    return (
      <FormField fullWidth label={label} error={error}>
        {description && (
          <span class="text-tertiary text-xs">{description}</span>
        )}

        <div
          class="flex space-x-2"
          onPaste$={[
            sync$((event: ClipboardEvent, target: HTMLDivElement) => {
              event.preventDefault();
              const paste = event.clipboardData?.getData("text");
              if (paste) {
                target.dataset.pastedContent = paste;
              }
            }),
            $((_, target: HTMLDivElement) => {
              const paste = target.dataset.pastedContent;
              if (!paste) return;

              // Clean up data attribute
              delete target.dataset.pastedContent;

              // Extract only digits
              const digits = paste.replace(/\D/g, "");
              if (!digits) return;

              const newCode = Array(length).fill("");
              for (let i = 0; i < Math.min(digits.length, length); i++) {
                newCode[i] = digits[i];
              }

              code.value = newCode;
              onChange$?.(newCode);

              if (newCode.every(isDigit)) {
                onComplete$?.(newCode.join(""));
              }

              focusInput(Math.min(digits.length, length) - 1);
            }),
          ]}
        >
          {code.value.map((digit, index) => (
            <Input
              type="text"
              key={index}
              maxLength={1}
              value={digit}
              ref={$((el: HTMLInputElement) => {
                inputRefs.value[index] = el;
              })}
              aria-label={`Digit ${index + 1}`}
              onInput$={(event) => handleInput(event, index)}
              onKeyDown$={(event) => handleKeyDown(event, index)}
              class="h-10 text-center sm:h-20"
            />
          ))}
        </div>
      </FormField>
    );
  },
);
